import {TaCommentDetails} from '../api';

/**
 * Display helpers for the TA comment chain.
 *
 * The chain itself is the generated TaCommentDetails: a comment is its text plus the three
 * UserEvents that describe what has happened to it - `creation` (who wrote it and when), and
 * `editEvent` / `deleteEvent` once it has been changed or removed. The author lives on `creation`,
 * so everything below reads from there rather than from a user field of its own.
 */

/** The author's id, or undefined on a comment the server sent without a creation event. */
export function commentAuthorId(comment: TaCommentDetails): string | undefined {
  return comment.creation?.createdById ?? undefined;
}

/** The author's display name, falling back to something printable for the avatar tooltip. */
export function commentAuthorName(comment: TaCommentDetails): string {
  return comment.creation?.createdByName?.trim() || 'Unknown user';
}

/**
 * The avatar's tooltip: the author's name, with their address under it. Two lines rather than one
 * run - an address is as long as the name is short, and side by side they make a tooltip wider than
 * the bubble it belongs to. The newline renders because PrimeNG's .p-tooltip-text is already
 * white-space: pre-line; the width it needs to hold an address is set on .ta-comment-user-tooltip
 * in styles.css. Nothing to add when the server sent no address.
 */
export function commentAuthorTooltip(comment: TaCommentDetails): string {
  const name = commentAuthorName(comment);
  const email = comment.creation?.createdByEmail?.trim();
  return email ? `${name}\n(${email})` : name;
}

/**
 * True once the comment has been removed. A removed comment stays in the chain as a tombstone
 * rather than disappearing out from under whoever is reading it; the server blanks `text` at the
 * same time, so the words never reach the client again.
 */
export function isCommentRemoved(comment: TaCommentDetails): boolean {
  return comment.isDeleted === true;
}

/**
 * Two letters for the avatar, derived from the author's name - a UserEvent carries the name but no
 * initials, so there is nothing server-side to prefer here.
 */
export function commentInitials(comment: TaCommentDetails): string {
  const parts = (comment.creation?.createdByName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/**
 * The @mention format that lives inside a comment's text, and the conversions either side of it.
 *
 * A mention is stored inline in the comment text as `@[Display Name](userId)`, so a comment stays
 * one string and nothing else has to be saved alongside it. The id is what identifies the person -
 * it is what a notification or a permission check would key off - and the name travels beside it,
 * captured at the moment the mention is made, so rendering a comment needs no directory and no
 * lookup. A chain can draw its mentions before it knows who anybody is.
 *
 * That gives three forms of the same comment, and a conversion between each pair:
 *   stored    `Ask @[Ron Bean](a3f1c8d2-...) about the flights`  - what the server holds
 *   display   `Ask @Ron Bean about the flights`                  - what sits in the composer
 *   segments  [text, mention, text]                                   - what the chain renders
 *
 * The cost of carrying the name is that it is a snapshot: someone renamed after being mentioned
 * keeps the name they had, and a user who hand-types a well-formed token gets a mention card under
 * a name of their choosing. Both are accepted rather than defended against - every comment is
 * stamped with its own author, and the fix for either is the server rewriting names by id as a
 * comment is saved, not more parsing here on the way out.
 *
 * Everything here is pure; the composer supplies the list of who can be mentioned.
 */

/** A person who can be mentioned - one row of the composer's picker, straight from the directory. */
export interface MentionPerson {
  id: string;
  name: string;
  email?: string;
  initials?: string;
}

/**
 * A mention already written into a comment: an id, and the name it was made under.
 *
 * Separate from MentionPerson because these are read back out of the text rather than out of the
 * directory, so there is no address or initials to be had. Both of those are optional on
 * MentionPerson, so without this distinction reading one off a mention would compile and quietly
 * render nothing.
 */
export type MentionRef = Pick<MentionPerson, 'id' | 'name'>;

/** One run of a comment, once the mentions have been picked out of it. */
export type CommentSegment =
  | {kind: 'text'; text: string}
  | {kind: 'mention'; userId: string; name: string};

/**
 * The stored form. The id has to match a GUID exactly, so `@[foo](bar)` typed by hand is left alone
 * as text rather than swallowed as a broken mention, and the name has to be non-empty, so `@[](id)`
 * is not a mention with nothing to show for itself.
 */
const GUID_PATTERN = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const MENTION_TOKEN_PATTERN = new RegExp(`@\\[([^\\]\\n]+)\\]\\((${GUID_PATTERN})\\)`, 'g');

/**
 * The half-typed mention under the caret: an `@` at the start of the text or after a space, and
 * everything up to the caret. Spaces are allowed inside it so "@Ron B" still finds Ron Bean;
 * the length cap and "no candidates closes the list" are what stop it running on into prose.
 */
const MENTION_QUERY_PATTERN = /(?:^|\s)@([^@\n]{0,40})$/;

/** How many people the picker offers at once. */
const MAX_SUGGESTIONS = 8;

/**
 * The stored form of one mention.
 *
 * The strip is not tidiness. A name containing `]` would close the name early and let the rest of
 * itself be read as the token's id: a display name of `[Boss](00000000-0000-0000-0000-000000000000)`
 * would produce a mention card pointing at a guid of the name's choosing, with the real id left
 * behind as literal text. Newlines go for the same reason - the pattern is single-line.
 *
 * This is the only place a name is cleaned. Doing it again where a MentionPerson is built would be
 * a second place to keep in step, and buys nothing: a stripped name reaching the field is harmless,
 * because what the field shows is only ever compared against itself.
 */
export function mentionToken(userId: string, name: string): string {
  return `@[${name.replace(/[\]\r\n]/g, '')}](${userId})`;
}

const WORKFLOW_TAG_PATTERN = /^#\[workflow\]\(([a-z-]+)\)\s*/;

/**
 * A workflow note (submit, approve, withhold, ...) is stored as a comment prefixed with
 * `#[workflow](key) `. The key names the note; its label lives in app.types.
 *
 * The server stores an edit verbatim, so the client takes the tag off before editing and puts it
 * back on the way out - see withWorkflowTag.
 */
export function splitWorkflowTag(text: string | null | undefined): {key?: string; body: string} {
  const source = text ?? '';
  const match = WORKFLOW_TAG_PATTERN.exec(source);
  return match ? {key: match[1], body: source.slice(match[0].length)} : {body: source};
}

/** The stored form of a workflow note: the tag back in front of its body. */
export function withWorkflowTag(key: string | undefined, body: string): string {
  return key ? `#[workflow](${key}) ${body}` : body;
}

/** Stored text, split into the runs the chain renders. */
export function parseCommentSegments(text: string | null | undefined): CommentSegment[] {
  const source = text ?? '';
  const segments: CommentSegment[] = [];
  let cursor = 0;

  for (const match of source.matchAll(MENTION_TOKEN_PATTERN)) {
    const at = match.index ?? 0;
    if (at > cursor) {
      segments.push({kind: 'text', text: source.slice(cursor, at)});
    }
    // Group 1 is the name and group 2 the id - in this format the id is the second of the two.
    segments.push({kind: 'mention', userId: match[2], name: match[1]});
    cursor = at + match[0].length;
  }

  if (cursor < source.length) {
    segments.push({kind: 'text', text: source.slice(cursor)});
  }
  return segments;
}

/** Stored text as the composer shows it - every mention flattened back to `@Name`. */
export function toDisplayText(text: string | null | undefined): string {
  return parseCommentSegments(text)
    .map(segment => segment.kind === 'mention' ? `@${segment.name}` : segment.text)
    .join('');
}

/**
 * The mentions a stored comment carries, in the order they appear. This is what re-seeds the
 * composer when it opens on a comment that already has some.
 *
 * Deduped on id *and* name rather than id alone: one comment can hold the same person under two
 * names, having been edited either side of a rename. Dropping one of them would leave that `@Name`
 * with nothing to match, and it would quietly become plain text on the next edit. A repeated id is
 * harmless - everything downstream matches on the name.
 */
export function mentionsIn(text: string | null | undefined): MentionRef[] {
  const found = new Map<string, MentionRef>();
  for (const segment of parseCommentSegments(text)) {
    if (segment.kind === 'mention') {
      found.set(`${segment.userId} ${segment.name}`, {id: segment.userId, name: segment.name});
    }
  }
  return [...found.values()];
}

/**
 * Display text back to stored text. Only the mentions in `accepted` - the ones picked from the
 * composer's list, plus the ones the comment already carried - are written back as tokens, so an
 * `@Name` that was typed out by hand, or one the user has since edited into something else, stays
 * plain text.
 *
 * Scanned rather than search-and-replaced so a name can never be matched inside a token that has
 * already been written, and longest name first so "@Ron Bean" is not matched as "@Ron". Two
 * different people with the same name resolve to whichever was accepted first: the display text is
 * only `@Name`, so there is nothing left in it to tell them apart, even though the stored text they
 * came from could.
 */
export function toStoredText(displayText: string, accepted: readonly MentionRef[]): string {
  if (accepted.length === 0) {
    return displayText;
  }
  const byLongestName = [...accepted].sort((a, b) => b.name.length - a.name.length);

  let stored = '';
  let index = 0;
  while (index < displayText.length) {
    if (displayText[index] === '@') {
      const person = byLongestName.find(candidate => displayText.startsWith(candidate.name, index + 1));
      if (person) {
        stored += mentionToken(person.id, person.name);
        index += 1 + person.name.length;
        continue;
      }
    }
    stored += displayText[index];
    index++;
  }
  return stored;
}

/** The accepted mentions still present in the display text - the rest have been edited away. */
export function prunedAccepted(displayText: string, accepted: readonly MentionRef[]): MentionRef[] {
  return accepted.filter(person => displayText.includes(`@${person.name}`));
}

/**
 * The `@Name` run a delete keystroke should take out whole, if the caret is in or against one.
 *
 * The composer draws its accepted mentions as cards and removes those whole by the element, so this
 * is the fallback for the runs that are still loose text there: a name typed out by hand that
 * matches one already accepted is a token once stored, and has to delete like the mention it will
 * be rather than a letter at a time. Names that are not accepted are text, and delete like text.
 *
 * Backspace covers a caret inside the run or just after it, Delete a caret inside it or just before.
 * Longest name first, so `@Ron Bean` is not matched as `@Ron`.
 */
export function mentionRunToDelete(
  displayText: string,
  caret: number,
  direction: 'backward' | 'forward',
  accepted: readonly MentionRef[],
): {start: number; end: number} | undefined {
  const byLongestName = [...accepted].sort((a, b) => b.name.length - a.name.length);

  for (const person of byLongestName) {
    const run = `@${person.name}`;
    let start = displayText.indexOf(run);
    while (start !== -1) {
      const end = start + run.length;
      const hit = direction === 'backward'
        ? caret > start && caret <= end
        : caret >= start && caret < end;
      if (hit) {
        return {start, end};
      }
      start = displayText.indexOf(run, start + 1);
    }
  }
  return undefined;
}

/**
 * The mention being typed at the caret, if any. `start` is the index of the `@` itself, so the
 * caller can replace the whole run once a person is picked.
 */
export function mentionQueryAt(text: string, caret: number): {query: string; start: number} | undefined {
  const match = MENTION_QUERY_PATTERN.exec(text.slice(0, caret));
  if (!match) {
    return undefined;
  }
  const query = match[1];
  return {query, start: caret - query.length - 1};
}

/**
 * Who to offer for a query. An empty query - the moment `@` is typed - offers everyone, which is
 * what makes the picker browsable rather than only searchable.
 */
export function matchMentionPeople(
  people: readonly MentionPerson[],
  query: string,
  limit: number = MAX_SUGGESTIONS,
): MentionPerson[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return people.slice(0, limit);
  }
  return people
    .filter(person => person.name.toLowerCase().includes(needle)
      || (person.email ?? '').toLowerCase().includes(needle))
    .slice(0, limit);
}

/** Two letters for the picker's avatar, preferring the initials the server already worked out. */
export function mentionInitials(person: MentionPerson): string {
  if (person.initials?.trim()) {
    return person.initials.trim().toUpperCase();
  }
  const parts = person.name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (parts[0][0] + last).toUpperCase();
}
